import { ElectionPageParams, ElectionTime } from "@/src/common/types"
import { raceHasElectionInTime, raceIsRepresentative } from "@/src/common/utils"
import PresidentElectionCtx from "@/src/contexts/PresidentElectionCtx"
import { URL_SLUG_TO_RACE_HASH_MAP } from "@/src/elections/business-layer/constants"
import {
  findAndRemoveStateHouseTimeDataBefore2023,
  parseRawTimelineData
} from "@/src/elections/business-layer/selectors"
import {
  BaseElectionTemplateProps,
  MapTemplateProps,
  Race
} from "@/src/elections/business-layer/types"
import { GetElectionPropsUsecase } from "@/src/elections/business-layer/usecases"
import ElectionTemplate from "@/src/elections/components/ElectionTemplate"
import {
  Data,
  ElectionsAPIInstance,
  ExternalHTTPInstance
} from "@/src/elections/data-layer"
import stateBoundaries from "@/src/elections/data-layer/geojson/state-houses-of-assembly-state-boundaries.json"
import stateHouse from "@/src/elections/data-layer/geojson/state-houses-of-assembly.json"
import { ElectionSources2023 } from "@/src/elections/data-layer/types"
import { FeatureCollection } from "geojson"
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage
} from "next"
import { useState } from "react"

const RaceSummary: NextPage<BaseElectionTemplateProps> = (
  props: BaseElectionTemplateProps
) => {
  const [electionSource, setElectionSource] = useState(ElectionSources2023.TV)

  const mapProps = {
    ...props.mapProps,
    appendStateName: true,
    stateBoundaries: stateBoundaries as FeatureCollection,
    mapFeatures: stateHouse as FeatureCollection
  } as MapTemplateProps

  return (
    <PresidentElectionCtx.Provider
      value={{ source: electionSource, setSource: setElectionSource }}
    >
      <ElectionTemplate
        templateProps={{ ...props, mapProps }}
        electionsAPI={ElectionsAPIInstance}
        externalHTTP={ExternalHTTPInstance}
      />
    </PresidentElectionCtx.Provider>
  )
}

export async function getStaticPaths() {
  const timelines = await ElectionsAPIInstance.fetchElectionsDataTimeline()
  return {
    paths: Data.upcomingElections.reduce(
      (electionsYearRace, election) => [
        ...electionsYearRace,
        ...parseRawTimelineData(timelines)
          .map((time: ElectionTime) => {
            time.races = { state_house: time.races.state_house }
            return time
          })
          .map(findAndRemoveStateHouseTimeDataBefore2023)
          .filter((time) => raceHasElectionInTime(time, election.slug as Race))
          .map((timeline) => {
            return {
              params: {
                year: timeline.year,
                raceUrlSlug: "state-houses-of-assembly"
              }
            }
          })
      ],
      [] as Array<{
        params: {
          year: string
          raceUrlSlug: string
        }
      }>
    ),
    fallback: false
  }
}

export async function getStaticProps(
  context: GetStaticPropsContext<ElectionPageParams>
): Promise<GetStaticPropsResult<BaseElectionTemplateProps>> {
  const raceUrlSlug = "state-houses-of-assembly"
  const race = URL_SLUG_TO_RACE_HASH_MAP[raceUrlSlug]

  const getElectionPropsUsecase = new GetElectionPropsUsecase()
  const props = await getElectionPropsUsecase.execute(
    raceUrlSlug,
    context.params?.state,
    raceIsRepresentative(race) ? "constituency_code" : "state_code",
    context.params?.year
  )

  if (props.electionIndex === -1) {
    return {
      notFound: true
    }
  }

  return {
    revalidate: false,
    props
  }
}

export default RaceSummary
